<template>
  <div
    class="fu-fcards"
    :class="{ 'fu-fcards--static': !clickable }"
    :style="{ '--fu-fcards-cols': cards.length || 1 }"
    role="group"
    :aria-label="label"
    data-test="fu-filter-cards"
  >
    <component
      :is="clickable ? 'button' : 'div'"
      v-for="c in cards"
      :key="c.key"
      :type="clickable ? 'button' : null"
      class="fu-fcard"
      :class="{ 'fu-fcard--on': clickable && c.key === active, 'fu-fcard--button': clickable }"
      :aria-pressed="clickable ? (c.key === active ? 'true' : 'false') : null"
      :data-test="`fu-filter-card-${c.key}`"
      @click="clickable && $emit('select', c.key === active ? null : c.key)"
    >
      <span class="fu-fcard__top">
        <span v-if="c.dot" class="fu-fcard__dot" :class="`fu-fcard__dot--${c.dot}`" aria-hidden="true"></span>
        <span class="fu-fcard__label">{{ c.label }}</span>
        <span v-if="clickable && c.key === active" class="fu-fcard__tag" data-test="fu-filter-card-on">Filtering</span>
      </span>
      <span class="fu-fcard__value" data-test="fu-filter-card-value">{{ c.value }}</span>
      <span class="fu-fcard__sub" data-test="fu-filter-card-sub"><strong v-if="c.subStrong" class="fu-fcard__strong">{{ c.subStrong }}</strong>{{ c.subStrong ? ' ' : '' }}{{ c.sub }}</span>
    </component>
  </div>
</template>

<script>
/**
 * A row of count cards that can narrow the table under them (redesign Phase 4) — the export's
 * Aging bucket cards (lines 265–312) and Broken appointments period cards (lines 313–334).
 *
 *   <followups-filter-cards :cards="fuBucketCards" :active="fuBucket" label="Claims by age"
 *     @select="key => fuBucket = key" />
 *
 *   cards     [{ key, label, value, sub, subStrong?, dot? }] — the numbers come from
 *             src/utils/followups/filterCards.js; this only draws them. `dot` is one of
 *             brand / amber / orange / red.
 *   active    the key of the card that is filtering, or null
 *   clickable false draws plain cards (Patient balances — not clickable in the export)
 *   label     the group's accessible name
 *
 * A click emits select(key), or select(null) on the card that is already on (a second click lets
 * go, as in the export). The page owns what "filtering" means — this never touches rows.
 * Presentational: props in, events out.
 */
export default {
  name: 'FollowupsFilterCards',
  props: {
    cards: { type: Array, default: () => [] },
    active: { type: String, default: null },
    clickable: { type: Boolean, default: true },
    label: { type: String, default: 'Filter cards' },
  },
};
</script>

<style lang="scss" scoped>
@import './mixins';

.fu-fcards {
  display: grid;
  grid-template-columns: repeat(var(--fu-fcards-cols), minmax(0, 1fr));
  gap: $fu-space-4;
  margin-bottom: $fu-space-4;
}

.fu-fcard {
  @include fu-card($fu-radius-lg);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-width: 0;
  padding: $fu-space-4 18px;
  text-align: left;
  font-family: $fu-font-sans;
  color: $fu-text;
}

.fu-fcard--button {
  @include fu-button-reset;
  @include fu-card($fu-radius-lg);
  @include fu-focus-ring;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: $fu-space-4 18px;
  transition: box-shadow 0.15s ease, transform 0.15s ease, border-color 0.15s ease;
  &:hover { box-shadow: $fu-shadow-md; transform: translateY(-1px); }
}

.fu-fcard--on,
.fu-fcard--on:hover {
  border-color: $fu-brand;
  box-shadow: $fu-ring;
  transform: none;
}

.fu-fcard__top {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
}

.fu-fcard__dot {
  @include fu-status-dot;
}

.fu-fcard__dot--brand { background: $fu-brand-400; }
.fu-fcard__dot--amber { background: $fu-warning; }
.fu-fcard__dot--orange { background: $fu-orange; }
.fu-fcard__dot--red { background: $fu-danger; }

.fu-fcard__label {
  @include fu-eyebrow($fu-text-muted);
  @include fu-ellipsis;
  min-width: 0;
}

.fu-fcard--on .fu-fcard__label {
  color: $fu-neutral-600;
}

.fu-fcard__tag {
  margin-left: auto;
  font-size: 11.5px;
  font-weight: $fu-fw-bold;
  color: $fu-brand-700;
  white-space: nowrap;
}

.fu-fcard__value {
  @include fu-font-display;
  @include fu-tabular;
  @include fu-ellipsis;
  display: block;
  margin-top: $fu-space-3;
  font-weight: 800;
  font-size: 28px;
  line-height: 1.05;
  letter-spacing: -0.03em;
}

.fu-fcard__sub {
  display: block;
  margin-top: $fu-space-2;
  font-size: 12.5px;
  line-height: 1.4;
  color: $fu-text-muted;
}

.fu-fcard__strong {
  font-weight: $fu-fw-bold;
  color: $fu-danger-text;
}

// Below 900px: two cards a row (the export's L.cols4 on a tablet / phone); a lone last card of an
// odd count simply sits on its own row.
@include fu-tablet {
  .fu-fcards { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@include fu-phone {
  .fu-fcards { gap: 10px; }
  .fu-fcard, .fu-fcard--button { padding: 14px; }
  .fu-fcard__value { font-size: 24px; }
}
</style>
